import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import {
  FidelityCardPointSimulationDto,
  FidelityCardTypeEnum,
  GetCustomerFidelityInfoInput,
  GetCustomerFidelityInfoNouveauPointInput,
} from 'src/app/services/fidelity/fidelity';
import { FidelityService } from 'src/app/services/fidelity/fidelity.service';
import { PaytweakRequestInput } from 'src/app/services/fidelity/paytweak';
import { Customer } from 'src/app/services/kingfisher/customer';
import { MODAL, ModalService } from 'src/app/services/modal.service';
import { ToastService } from 'src/app/services/toast.service';
import { VALIDATION_COMPONENT_TYPE_ENUM } from '../validation.component';
import { FidelityCardValidationModel } from 'src/app/models/configurators-2d/applications/kitchen/validation/base-validation-project-info';
import { StatisticTypeEnum } from 'src/app/enum/statistic-type.enum';
import { logError } from 'src/app/services/helpers/sentry.helper';
import { FeatureSettingsService } from 'src/app/services/feature-settings/feature-settings.service';

@Component({
  selector: 'ng-fidelity-card-validation',
  templateUrl: './fidelity-card-validation.component.html',
})
export class FidelityCardValidationComponent implements OnInit, OnDestroy {
  @Input() model$: Observable<FidelityCardValidationModel>;
  @Output() onRefreshRequested: EventEmitter<VALIDATION_COMPONENT_TYPE_ENUM> = new EventEmitter();

  customerSubject: BehaviorSubject<Customer> = new BehaviorSubject<Customer>(undefined);
  fidelityCardPointSimulationInfo: FidelityCardPointSimulationDto;
  isLoading: boolean = true;
  customerId: string;
  customer: Customer;
  amount: number;
  sidebarVisible: boolean = false;

  private subscription: Subscription;

  constructor(
    private readonly fidelityService: FidelityService,
    private readonly toastService: ToastService,
    private readonly translateService: TranslateService,
    private readonly featureSettingsService: FeatureSettingsService,
    public modalService: ModalService
  ) {}

  ngOnInit(): void {
    this.subscription = this.model$.subscribe(async model => {
      this.customerId = model.customer.kfId;
      this.customer = model.customer;
      this.customerSubject.next(this.customer);
      this.amount = model.amount;
      await this.getCustomerFidelityNouveauInfo(this.customerId);
    });
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  async getCustomerFidelityNouveauInfo(id: string) {
    if (!(await this.featureSettingsService.isFidelityEnabled())) {
      this.isLoading = false;
      return;
    }

    const input: GetCustomerFidelityInfoNouveauPointInput = {
      customerId: id,
      amount: Math.floor(this.amount),
    };
    this.isLoading = true;
    if (this.amount != undefined && this.customerId != null) {
      try {
        this.fidelityCardPointSimulationInfo = new FidelityCardPointSimulationDto(
          await this.fidelityService.getCustomerFidelityCardInfoNouveauPoint(input)
        );
      } catch (error) {
        logError(error);
      } finally {
        this.isLoading = false;
      }
    }
  }

  buildPaytweakRequestInput(): PaytweakRequestInput {
    let input: PaytweakRequestInput = {
      customerId: this.customer.kfId,
      cardNumber: this.fidelityCardPointSimulationInfo.cardNumber,
      firstname: this.customer.firstname,
      lastname: this.customer.lastname,
      address: this.customer.address.line1,
      postalCode: this.customer.address.postalCode,
      city: this.customer.address.city,
      country: this.customer.address.country,
      phone: this.customer.phoneNumber,
      email: this.customer.email,
    };

    return input;
  }

  async showPaytweakQrCode() {
    if (!(await this.featureSettingsService.isFidelityEnabled())) {
      return;
    }

    let input: PaytweakRequestInput = this.buildPaytweakRequestInput();

    this.modalService.display(MODAL.FIDELITY_PAYMENT_QRCODE, {
      paytweakRequestInput: input,
      onCloseCallback: undefined,
      refreshValidationRequested: this.onRefreshRequested,
    });
  }

  async postRenewPremiumCard() {
    if (!(await this.featureSettingsService.isFidelityEnabled())) {
      return;
    }

    const input: GetCustomerFidelityInfoInput = {
      idKingfisher: this.customerId,
    };
    if (this.customerId != null) {
      try {
        await this.fidelityService.postRenewPremiumCard(input);

        await this.showPaytweakQrCode();
      } catch (error) {
        this.toastService.showErrorToast(this.translateService.instant('popins.signup.error_title'), error.message);
        logError(error);
      } finally {
        this.isLoading = false;
      }
    }
  }

  displayRenewBtn(): boolean {
    if (!this.fidelityCardPointSimulationInfo?.cardFinalExpirationDate) return false;
    let today = new Date();

    let expirationDate = new Date(this.fidelityCardPointSimulationInfo.cardFinalExpirationDate);
    let dateToNotice = new Date();

    const NbDaysNoticeEndSubscriptionPaidCard = 30;

    //Ajout de la date de notification en fonction du setting
    dateToNotice.setTime(expirationDate.getTime() - NbDaysNoticeEndSubscriptionPaidCard * 24 * 60 * 60 * 1000);
    return today > dateToNotice;
  }

  displayLargeCard(): boolean {
    return (
      this.isPremium(this.fidelityCardPointSimulationInfo.type) ||
      this.isPremiumWaiting(this.fidelityCardPointSimulationInfo.type)
    );
  }

  isFreeCard(type: FidelityCardTypeEnum): boolean {
    return (
      type == FidelityCardTypeEnum.AUCUNE ||
      type == FidelityCardTypeEnum.GRATUITE ||
      type == FidelityCardTypeEnum.AUCUNE0
    );
  }

  isPremium(type: FidelityCardTypeEnum): boolean {
    return type == FidelityCardTypeEnum.PREMIUM;
  }

  isPremiumWaiting(type: FidelityCardTypeEnum): boolean {
    return type == FidelityCardTypeEnum.PREMIUMWAITING;
  }

  closeFidelity() {
    this.sidebarVisible = false;
  }

  async createFidelityCard(withFree: boolean = false, withPremium: boolean = false, cardIsPremium: boolean = false) {
    if (!(await this.featureSettingsService.isFidelityEnabled())) {
      return;
    }

    this.modalService.display(MODAL.REGISTER, {
      defaultEmail: this.customer.email,
      loadData: true,
      createCard: true,
      withFree: withFree,
      withPremium: withPremium,
      cardIsPremium: cardIsPremium,
      statisticType: StatisticTypeEnum.NEW_FREE_CARD_VALIDATION,
      updateDataValidation: true,
      updateData: this.fidelityCardPointSimulationInfo.hasCard,
      cardNumber: this.fidelityCardPointSimulationInfo.cardNumber,
      refreshValidationRequested: this.onRefreshRequested,
    });
  }

  refresh() {
    this.onRefreshRequested.emit();
  }
}
